import { roundMoneyDiv } from '@/app/utils/moneyRounding';
import { parseTenThousandths } from '@/shared/utils/moneyArithmetic';
import { vatFractionFromPercent } from '@/shared/utils/vatRate';
import type { QuoteProductFee, QuoteProductFeeBand } from '../types/products.types';

interface RawProductFeeBand {
    band_index?: unknown;
    cost_net?: unknown;
    cost_gross?: unknown;
    percentage?: unknown;
}

interface RawProductFee {
    id?: unknown;
    is_absorbed?: unknown;
    application_scope?: unknown;
    cap_value_gross?: unknown;
    default_cost_net?: unknown;
    default_cost_gross?: unknown;
    default_percentage?: unknown;
    value_type?: { code?: unknown; is_percentage?: unknown; requires_lookup?: unknown } | null;
    vat_category?: { rate_percent?: unknown } | null;
    bands?: unknown;
}

const toNumberOrNull = (value: unknown): number | null => {
    if (value === null || value === undefined || value === '') {
        return null;
    }
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
};

const requireVatRatePercent = (feeId: string, rawRatePercent: unknown): { vatRatePercent: number } => {
    const fraction = typeof rawRatePercent === 'string' ? vatFractionFromPercent(rawRatePercent) : null;
    if (fraction === null) {
        throw new Error(`Product fee ${feeId} has no VAT category rate`);
    }
    return { vatRatePercent: Number(rawRatePercent) };
};

const toMinorOrNull = (value: unknown): number | null => {
    const amount = typeof value === 'number' && Number.isFinite(value) ? String(value) : value;
    const tenThousandths = typeof amount === 'string' ? parseTenThousandths(amount) : null;
    return tenThousandths === null ? null : Number(roundMoneyDiv(tenThousandths, 100n));
};

const parseFeeBand = (raw: RawProductFeeBand): QuoteProductFeeBand | null => {
    const bandIndex = toNumberOrNull(raw.band_index);
    if (bandIndex === null) {
        return null;
    }
    return {
        bandIndex,
        costNetMinor: toMinorOrNull(raw.cost_net),
        costGrossMinor: toMinorOrNull(raw.cost_gross),
        percentage: toNumberOrNull(raw.percentage),
    };
};

const parseProductFee = (raw: RawProductFee): QuoteProductFee | null => {
    if (typeof raw.id !== 'string') {
        return null;
    }
    return {
        id: raw.id,
        valueTypeCode: typeof raw.value_type?.code === 'string' ? raw.value_type.code : '',
        isPercentage: raw.value_type?.is_percentage === true,
        requiresLookup: raw.value_type?.requires_lookup === true,
        isAbsorbed: raw.is_absorbed === true,
        applicationScope: raw.application_scope === 'per_service' ? 'per_service' : 'per_plan',
        defaultNetMinor: toMinorOrNull(raw.default_cost_net),
        defaultGrossMinor: toMinorOrNull(raw.default_cost_gross),
        defaultPercentage: toNumberOrNull(raw.default_percentage),
        capGrossMinor: toMinorOrNull(raw.cap_value_gross),
        ...requireVatRatePercent(raw.id, raw.vat_category?.rate_percent),
        bands: Array.isArray(raw.bands)
            ? (raw.bands as RawProductFeeBand[]).map(parseFeeBand).filter((band): band is QuoteProductFeeBand => band !== null)
            : [],
    };
};

export const parseProductFees = (rawFees: unknown): QuoteProductFee[] =>
    Array.isArray(rawFees)
        ? (rawFees as RawProductFee[]).map(parseProductFee).filter((fee): fee is QuoteProductFee => fee !== null)
        : [];
